import React, { useState } from 'react';
import styles from './DetalhesCard.module.css';

function StatusDot({ status }) {
  const map = { ok: styles.ok, atencao: styles.atencao, alerta: styles.alerta, critico: styles.critico };
  const labels = { ok: 'OK', atencao: 'Atenção', alerta: 'Alerta', critico: 'Crítico' };
  return (
    <span className={`${styles.dot} ${map[status] || styles.alerta}`}>
      {labels[status] || status}
    </span>
  );
}

/* ============================================================
   MODAL COMPONENT
   ============================================================ */
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.modalBody}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
  RECEITA MODAL
   ============================================================ */
function ReceitaModal({ isOpen, onClose, pessoas }) {
  const itens = pessoas && pessoas.length > 0 ? pessoas : [{ cpf: '—', situacao_cpf: '—', codigo_controle_receita_federal: '—' }];
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="CPF na Receita Federal">
        <div className={styles.modalTableWrapper}>
          <table className={styles.modalTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>CPF</th>
                <th>Situação</th>
                <th>Codigo da Receita</th>
              </tr>
            </thead>
            
            <tbody>
              {itens.map((pessoa, index) => (
                <tr key={index}>
                  <td className={styles.mono}>{index + 1}</td>
                  <td className={styles.mono}>{pessoa.cpf}</td>
                  <td>{pessoa.situacao_cpf || '—'}</td>
                  <td>{pessoa.codigo_controle_receita_federal || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </Modal>
  );
}

/* ============================================================
  EMAILS MODAL
   ============================================================ */
function EmailsModal({ isOpen, onClose, emails }) {
  const prioridadeOrder = {
    ALTA: 1,
    MEDIA: 2,
    BAIXA: 3,
  };

  const emailsOrdenados = [...(emails || [])].sort((a, b) => {
    const prioridadeA = prioridadeOrder[a?.prioridade?.toUpperCase()] || 999;
    const prioridadeB = prioridadeOrder[b?.prioridade?.toUpperCase()] || 999;

    return prioridadeA - prioridadeB;
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="✉️ Emails vinculados">
      {(!emailsOrdenados || emailsOrdenados.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum email encontrado.</p>
      ) : (
        <div className={styles.modalTableWrapper}>
          <table className={styles.modalTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Tipo</th>
                <th>Prioridade</th>
                <th>Categoria</th>
              </tr>
            </thead>
            <tbody>
              {emailsOrdenados.map((e, i) => (
                <tr key={i}>
                  <td className={styles.mono}>{i + 1}</td>
                  <td className={styles.mono}>{e.email}</td>
                  <td>{e.tipo_email || '—'}</td>
                  <td>
                    <span
                      className={`${styles.pill} ${
                        styles['pill' + (e.prioridade || 'MEDIA')]
                      }`}
                    >
                      {e.prioridade || '—'}
                    </span>
                  </td>
                  <td>{e.categoria || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   TELEFONES MODAL
   ============================================================ */
function TelefonesModal({ isOpen, onClose, telefones }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📞 Telefones vinculados">
      {(!telefones || telefones.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum telefone encontrado.</p>
      ) : (
        <div className={styles.modalTableWrapper}>
          <table className={styles.modalTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Telefone</th>
                <th>Tipo</th>
                <th>Prioridade</th>
                <th>Categoria</th>
              </tr>
            </thead>
            <tbody>
              {telefones.map((t, i) => (
                <tr key={i}>
                  <td className={styles.mono}>{i + 1}</td>
                  <td className={styles.mono}>{t.telefone_completo || `(${t.ddd}) ${t.telefone}`}</td>
                  <td>{t.tipo_telefone || '—'}</td>
                  <td>
                    <span className={`${styles.pill} ${styles['pill' + (t.prioridade || 'MEDIA')]}`}>
                      {t.prioridade || '—'}
                    </span>
                  </td>
                  <td>{t.categoria || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   ENDEREÇOS MODAL
   ============================================================ */
function EnderecosModal({ isOpen, onClose, enderecos }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📍 Endereços">
      {(!enderecos || enderecos.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum endereço encontrado.</p>
      ) : (
        <div className={styles.modalList}>
          {enderecos.map((e, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
                <span className={`${styles.pill} ${styles['pill' + (e.prioridade || 'MEDIA')]}`}>
                  {e.prioridade || 'MÉDIA'}
                </span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Logradouro:</strong> {e.tipo_logradouro ? `${e.tipo_logradouro} ` : ''}{e.logradouro || '—'}
                  {e.numero ? `, ${e.numero}` : ''}
                  {e.complemento ? ` — ${e.complemento}` : ''}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Bairro:</strong> {e.bairro || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Município:</strong> {e.municipio || '—'} — <strong>UF:</strong> {e.uf || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CEP:</strong> <span className={styles.mono}>{e.cep || '—'}</span>
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Categoria:</strong> {e.categoria || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Atividades Ocupacionais MODAL
   ============================================================ */
function OcupacoesModal({ isOpen, onClose, ocupacoes }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="💼 Atividades Ocupacionais">
      {(!ocupacoes || ocupacoes.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhuma atividade ocupacional encontrada.</p>
      ) : (
        <div className={styles.modalList}>
          {ocupacoes.map((o, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Grupo:</strong> {o.grupo || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Classificação Brasileira de Ocupação:</strong> {o.cbo || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Descrição:</strong> {o.descricao || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Atividades Economicas MODAL
   ============================================================ */
function EconomicasModal({ isOpen, onClose, economicas }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🏦 Atividades Economicas">
      {(!economicas || economicas.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhuma atividade economica encontrada.</p>
      ) : (
        <div className={styles.modalList}>
          {economicas.map((e, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>CNAE:</strong> {e.cnae || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Descrição:</strong> {e.descricao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Tipo de Atividade:</strong> {e.tipo_atividade || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Socios Pessoa Física MODAL
   ============================================================ */
function SociosPfModal({ isOpen, onClose, socios }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🏦 Socios Pessoa Física">
      {(!socios || socios.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum sócio pessoa física encontrado.</p>
      ) : (
        <div className={styles.modalList}>
          {socios.map((s, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Nome:</strong> {s.nome_socio || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CPF:</strong> {s.cpf_socio || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Socios Pessoa Jurídica MODAL
   ============================================================ */
function SociosPjModal({ isOpen, onClose, socios }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🏦 Socios Pessoa Jurídica">
      {(!socios || socios.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum sócio pessoa jurídica encontrado.</p>
      ) : (
        <div className={styles.modalList}>
          {socios.map((s, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Nome:</strong> {s.razao_social || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CNPJ:</strong> {s.cnpj_socio || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Socios Extrangeiros MODAL
   ============================================================ */
function SociosExtrangeirosModal({ isOpen, onClose, socios }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🏦 Socios Extrangeiros">
      {(!socios || socios.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum sócio extrangeiro encontrado.</p>
      ) : (
        <div className={styles.modalList}>
          {socios.map((s, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Nome do Representante:</strong> {s.nome_representante_legal || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CPF do Representante:</strong> {s.cpf_representante_legal || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Pessoa Politicamente Exposta (PEP) MODAL
   ============================================================ */
function PepModal({ isOpen, onClose, pep }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🏛️ Pessoa Politicamente Exposta">
      {(!pep || pep.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhuma pessoa politicamente exposta encontrada.</p>
      ) : (
        <div className={styles.modalList}>
          {pep.map((p, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Sigla da Função:</strong> {p.sigla_funcao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Descrição da Função:</strong> {p.descricao_funcao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Nível:</strong> {p.nivel_funcao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Nome do Órgão:</strong> {p.nome_orgao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data Início do Exercício:</strong> {p.data_inicio_exercicio || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data Fim do Exercício:</strong> {p.data_fim_exercicio || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data Fim da Carencia:</strong> {p.data_fim_carencia || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   Mandados de Prisão MODAL
   ============================================================ */
function MandadosModal({ isOpen, onClose, mandados }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🚨 Mandados de Prisão">
      {(!mandados || mandados.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum mandado de prisão encontrado.</p>
      ) : (
        <div className={styles.modalList}>
          {mandados.map((m, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Data da Consulta:</strong> {m.data_consulta || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Numero:</strong> {m.numero || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Alcunha:</strong> {m.alcunha || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Peça:</strong> {m.peca || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Situação:</strong> {m.situacao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data de Expedição:</strong> {m.data_expedicao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Órgão Expedidor:</strong> {m.orgao_expedidor || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   SOCIEDADES MODAL
   ============================================================ */
function SociedadesModal({ isOpen, onClose, sociedades }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🏢 Sociedades">
      {(!sociedades || sociedades.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhuma sociedade encontrada.</p>
      ) : (
        <div className={styles.modalList}>
          {sociedades.map((s, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
                {s.mandado_prisao === 'CONSTA OCORRENCIA' && (
                  <span className={`${styles.pill}`}>
                    🚨
                  </span>
                )}
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Nome:</strong> {s.nome || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Razão Social:</strong> {s.razao_social || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CNPJ:</strong> <span className={styles.mono}>{s.cnpj || '—'}</span>
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CPF:</strong> <span className={styles.mono}>{s.cpf || '—'}</span>
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Idade:</strong> {s.idade || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data de Nascimento:</strong> {s.data_nascimento || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   PARTICIPAÇÕES MODAL
   ============================================================ */

   function ParticipacoesModal({ isOpen, onClose, participacoes }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📈 Participações">
      {(!participacoes || participacoes.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhuma participação encontrada.</p>
      ) : (
        <div className={styles.modalList}>
          {participacoes.map((p, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
                {p.mandado_prisao === 'CONSTA OCORRENCIA' && (
                  <span className={`${styles.pill}`}>
                    🚨
                  </span>
                )}
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Razão Social:</strong> {p.razao_social || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>CNPJ:</strong> <span className={styles.mono}>{p.cnpj || '—'}</span>
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Setor de Atividade:</strong> {p.setor_atividade || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Território:</strong> {p.territorio || '—'}, {p.pais || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Anos de Fundação:</strong> {p.anos_fundacao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data de Fundação:</strong> {p.data_fundacao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data de Entrada na Sociedade:</strong> {p.data_entrada_sociedade || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   VÍNCULOS EMPREGATÍCIOS MODAL
   ============================================================ */
function VinculosEmpregoModal({ isOpen, onClose, vinculos }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📝 Vínculos empregatícios">
      {(!vinculos || vinculos.length === 0) ? (
        <p className={styles.modalEmpty}>Nenhum vínculo empregatício encontrado.</p>
      ) : (
        <div className={styles.modalList}>
          {vinculos.map((v, i) => (
            <div key={i} className={styles.modalCard}>
              <div className={styles.modalCardHeader}>
                <span className={styles.modalCardNum}>#{i + 1}</span>
                <span className={`${styles.pill} ${v.situacao_cadastral === 'ATIVA' ? styles.pillALTA : styles.pillMEDIA}`}>
                  {v.situacao_cadastral || '—'}
                </span>
              </div>
              <div className={styles.modalCardBody}>
                <p className={styles.modalCardLine}>
                  <strong>Razão Social:</strong> {v.razao_social || '—'}
                </p>
                {v.nome_fantasia && (
                  <p className={styles.modalCardLine}>
                    <strong>Nome Fantasia:</strong> {v.nome_fantasia}
                  </p>
                )}
                <p className={styles.modalCardLine}>
                  <strong>CNPJ:</strong> <span className={styles.mono}>{v.cnpj_vinculo || '—'}</span>
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Tipo de relação:</strong> {v.tipo_relacao || '—'}
                </p>
                <p className={styles.modalCardLine}>
                  <strong>Data fundação:</strong> {v.data_fundacao || '—'}
                  {v.anos_fundacao ? ` (${v.anos_fundacao} anos)` : ''}
                </p>
                {v.setor_atividade && (
                  <p className={styles.modalCardLine}>
                    <strong>Setor:</strong> {v.setor_atividade}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================================================
   DIMENSION CARD (clickable)
   ============================================================ */
function DimensionCard({ title, data, icon, onClick, count }) {
  if (!data) return null;

  const status = data.status || 'alerta';
  const summaryText = data.texto || (count !== undefined ? `${count} registro(s)` : 'Ver detalhes');

  return (
    <div 
      className={`${styles.item} ${onClick ? styles.itemClickable : ''}`}
      onClick={onClick}
    >
      <div className={styles.itemHeader}>
        <div className={styles.itemHeaderLeft}>
          <span className={styles.itemIcon}>{icon}</span>
          <span className={styles.itemName}>{title}</span>
        </div>
        <div className={styles.itemHeaderRight}>
          {onClick && <span className={styles.clickHint}>Abrir →</span>}
        </div>
      </div>
      <span className={styles.itemVal}>{summaryText}</span>
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function DetalhesCard({ detalhes: d, sintese }) {
  const tipo = d?.cnpj ? 'PJ' : 'PF';
  const [modalTelefones, setModalTelefones] = useState(false);
  const [modalEmails, setModalEmails] = useState(false);
  const [modalReceita, setModalReceita] = useState(false);
  const [modalSociedades, setModalSociedades] = useState(false);
  const [modalOcupacoes, setModalOcupacoes] = useState(false);
  const [modalMandados, setModalMandados] = useState(false);
  const [modalParticipacoes, setModalParticipacoes] = useState(false);
  const [modalPEP, setModalPEP] = useState(false);
  const [modalEnderecos, setModalEnderecos] = useState(false);
  const [modalEmprego, setModalEmprego] = useState(false);
  const [modalSociosEx, setModalSociosEx] = useState(false);
  const [modalSociosPf, setModalSociosPf] = useState(false);
  const [modalSociosPj, setModalSociosPj] = useState(false);
  const [modalAtividadesEconomicas, setModalAtividadesEconomicas] = useState(false);


  if (!d && !sintese) return null;

  // Dados vêm de "sintese" (do JSON da DataSintese)
  const telefones = sintese?.telefones || [];
  const enderecos = sintese?.enderecos || [];
  const pessoas = sintese?.pessoa || {};
  const emails = sintese?.emails || [];
  const vinculosEmpresas = sintese?.vinculos_empresas || [];
  const ocupacoes = sintese?.ocupacoes || [];
  const sociedades = sintese?.socios || [];
  const participacoes = sintese?.participacoes || [];
  const mandados = sintese?.mandados_prisao || [];
  const pep = sintese?.pep || [];
  const vinculosPessoas = sintese?.vinculos_pessoas || [];


  const economicas = sintese?.atividade_economica || [];
  const sociosPf = sintese?.qsa_pf || [];
  const sociosPj = sintese?.qsa_pj || [];
  const sociosEx = sintese?.qsa_estrangeiro || [];

  const dimensions = [
    { 
      key: 'cpf', 
      title: 'CPF / Receita Federal', 
      icon: '🆔', 
      data: d?.cpf,
      type: 'PF',
      onClick: () => setModalReceita(true),
    },
    { key: 'obito', title: 'Suspeita de óbito', icon: '⚰️', data: d?.obito },
    { 
      key: 'mandados', 
      title: 'Mandados', 
      icon: '🚨', 
      data: d?.mandados || { status: mandados.length < 0 ? 'ok' : 'alerta', texto: `${mandados.length} mandado(s) de prisão` },
      type: 'PF',
      onClick: () => setModalMandados(true),
      count: mandados.length
    },
    { 
      key: 'telefones', 
      title: 'Telefones vinculados', 
      icon: '📞', 
      data: d?.telefones || { status: telefones.length > 0 ? 'ok' : 'alerta', texto: `${telefones.length} número(s)` },
      type: '',
      onClick: () => setModalTelefones(true),
      count: telefones.length
    },
    { 
      key: 'enderecos', 
      title: 'Endereços', 
      icon: '📍', 
      data: d?.enderecos || { status: enderecos.length > 0 ? 'ok' : 'alerta', texto: `${enderecos.length} endereço(s)` },
      type: '',
      onClick: () => setModalEnderecos(true),
      count: enderecos.length
    },
    { 
      key: 'emails', 
      title: 'Emails', 
      icon: '✉️', 
      data: d?.emails || { status: emails.length > 0 ? 'ok' : 'alerta', texto: `${emails.length} email(s)` },
      type: '',
      onClick: () => setModalEmails(true),
      count: emails.length
    },
    { 
      key: 'atividades', 
      title: 'Atividades Ocupacionais', 
      icon: '💼', 
      data: d?.emprego || { status: ocupacoes.length > 0 ? 'ok' : 'alerta', texto: `${ocupacoes.length} atividade(s)` },
      type: 'PF',
      onClick: () => setModalOcupacoes(true),
      count: ocupacoes.length
    },
    { 
      key: 'atividades', 
      title: 'Atividades Econômicas', 
      icon: '🏦', 
      data: d?.emprego || { status: economicas.length > 0 ? 'ok' : 'alerta', texto: `${economicas.length} atividade(s)` },
      type: 'PJ',
      onClick: () => setModalAtividadesEconomicas(true),
      count: economicas.length
    },
    { 
      key: 'emprego', 
      title: 'Vínculo empregatício', 
      icon: '📝', 
      data: d?.emprego || { status: vinculosEmpresas.length > 0 ? 'ok' : 'alerta', texto: `${vinculosEmpresas.length} vínculo(s)` },
      type: 'PF',
      onClick: () => setModalEmprego(true),
      count: vinculosEmpresas.length
    },
    { 
      key: 'participacoes', 
      title: 'Participações', 
      icon: '📈', 
      data: d?.emprego || { status: participacoes.length > 0 ? 'ok' : 'alerta', texto: `${participacoes.length} participação(s)` },
      type: 'PF',
      onClick: () => setModalParticipacoes(true),
      count: participacoes.length
    },
    { 
      key: 'sociedades', 
      title: 'Sociedades', 
      icon: '🏢', 
      data: d?.emprego || { status: sociedades.length > 0 ? 'ok' : 'alerta', texto: `${sociedades.length} sociedade(s)` },
      type: 'PF',
      onClick: () => setModalSociedades(true),
      count: sociedades.length
    },
    { 
      key: 'sociospf', 
      title: 'Sociedades Pessoa Física', 
      icon: '🏢', 
      data: d?.emprego || { status: sociosPf.length > 0 ? 'ok' : 'alerta', texto: `${sociosPf.length} sociedade(s)` },
      type: 'PJ',
      onClick: () => setModalSociosPf(true),
      count: sociosPf.length
    },
    { 
      key: 'sociospj', 
      title: 'Sociedades Pessoa Jurídica', 
      icon: '🏢', 
      data: d?.emprego || { status: sociosPj.length > 0 ? 'ok' : 'alerta', texto: `${sociosPj.length} sociedade(s)` },
      type: 'PJ',
      onClick: () => setModalSociosPj(true),
      count: sociosPj.length
    },
    { 
      key: 'sociosex', 
      title: 'Sociedades Extrangeiras', 
      icon: '🏢', 
      data: d?.emprego || { status: sociosEx.length > 0 ? 'ok' : 'alerta', texto: `${sociosEx.length} sociedade(s)` },
      type: 'PJ',
      onClick: () => setModalSociosEx(true),
      count: sociosEx.length
    },
    { 
      key: 'pep', 
      title: 'Pessoa Politicamente Exposta', 
      icon: '🏛️', 
      data: d?.emprego || { status: pep.length > 0 ? 'ok' : 'alerta', texto: `${pep.length} pessoa(s)` },
      type: 'PF',
      onClick: () => setModalPEP(true),
      count: pep.length
    },
    /* { key: 'veiculos', title: 'Veículos', icon: '🚗', data: d?.veiculos },
    { key: 'participacaoSocietaria', title: 'Participação societária', icon: '🏢', data: d?.participacaoSocietaria },
    { key: 'processos', title: 'Processos judiciais', icon: '⚖️', data: d?.processos },
    { key: 'protestos', title: 'Protestos', icon: '📋', data: d?.protestos },
    { key: 'cheques', title: 'Cheques sem fundo', icon: '💸', data: d?.cheques }, */
  ];

  const activeDimensions = dimensions.filter(dim => dim.data);

  return (
    <>
      <div className={styles.card}>
        <div className={styles.titulo}>
          Detalhamento por dimensão
          <span className={styles.tituloCount}>{activeDimensions.length} dimensões</span>
        </div>
        <div className={styles.grid}>
          {activeDimensions.filter(dim => dim.type === tipo || dim.type === '').map(dim => (
            <DimensionCard
              key={dim.key}
              title={dim.title}
              data={dim.data}
              icon={dim.icon}
              onClick={dim.onClick}
              count={dim.count}
            />
          ))}
        </div>
      </div>

      {/* Modais */}
      <ReceitaModal 
        isOpen={modalReceita} 
        onClose={() => setModalReceita(false)} 
        pessoas={pessoas} 
      />
      <OcupacoesModal 
        isOpen={modalOcupacoes} 
        onClose={() => setModalOcupacoes(false)} 
        ocupacoes={ocupacoes} 
      />
      <SociedadesModal 
        isOpen={modalSociedades} 
        onClose={() => setModalSociedades(false)} 
        sociedades={sociedades} 
      />
      <SociosPfModal
          isOpen={modalSociosPf}
          onClose={() => setModalSociosPf(false)}
          socios={sociosPf}
        />
      <SociosPjModal 
        isOpen={modalSociosPj} 
        onClose={() => setModalSociosPj(false)} 
        socios={sociosPj} 
      />
      <SociosExtrangeirosModal 
        isOpen={modalSociosEx} 
        onClose={() => setModalSociosEx(false)} 
        socios={sociosEx} 
      />  
      <EconomicasModal 
        isOpen={modalAtividadesEconomicas} 
        onClose={() => setModalAtividadesEconomicas(false)} 
        economicas={economicas} 
      />  
      <ParticipacoesModal 
        isOpen={modalParticipacoes} 
        onClose={() => setModalParticipacoes(false)} 
        participacoes={participacoes} 
      />
      <MandadosModal 
        isOpen={modalMandados} 
        onClose={() => setModalMandados(false)} 
        mandados={mandados} 
      />
      <PepModal 
        isOpen={modalPEP} 
        onClose={() => setModalPEP(false)} 
        pep={pep} 
      />
      <TelefonesModal 
        isOpen={modalTelefones} 
        onClose={() => setModalTelefones(false)} 
        telefones={telefones} 
      />
      <EmailsModal 
        isOpen={modalEmails} 
        onClose={() => setModalEmails(false)} 
        emails={emails} 
      />
      <EnderecosModal 
        isOpen={modalEnderecos} 
        onClose={() => setModalEnderecos(false)} 
        enderecos={enderecos} 
      />
      <VinculosEmpregoModal 
        isOpen={modalEmprego} 
        onClose={() => setModalEmprego(false)} 
        vinculos={vinculosEmpresas} 
      />
    </>
  );
}